import * as React from "react";
import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { ApplicationState } from "../../../store/index";
import * as services from "../../../store/service";
import { ezsServiceIds } from "../../../config/UserServicesList";
import { getActiveServiceCode } from "../../../util/CommonServices";
import * as CSIDActions from "../../../store/csid/actions";
import { IAddress } from "../../../store/csid/types";
import RenderIcons from "../../../Assets/css/_icons/partner_icon/RenderIcons";
import { useTranslation } from "react-i18next";
import parse from "html-react-parser";

interface IProps {
    closeButtonText: string;
    onCloseCallback: any;
    openAsDialog: boolean;
    submitButtonText: string;
    onSubmitCallback: any;
    errorCallback: any;
    ssn: string;
    dob?: string;
    address?: IAddress;
    postponeActivation: boolean;
    prepareForIDFL3: boolean;
    displayTimerForTimeout: boolean;
    timesInSecondBeforeTimeout: number; // initial = 120 (2 minutes)
    onTimeoutCallback: any;
    currentTryCount: number; // intial = 1
    maxTryCount: number; // intial = 2
    loadedResponse?: any;
    tuTrustedSessionId?: string;
}

interface EZSL3QuestionDTO {
    csidActivationStatus: string;
    authenticationStatus: string;
    numberOfTriesAttemptedStatus: string[];
    questions: any;
    hasError: boolean;
    errors: string[];
    isSuccess: boolean;
    caseId: string;
}

interface EZSL3SubmitResponseDTO {
    status: string;
    csidErrorText: string;
    csidErrorType: string;
    moreAuthenticationRequired: boolean;
    questions: any;
    hasError: boolean;
    errors: string[];
    isSuccess: boolean;
    caseId: string;
}

interface IDFL3QuestionDTO {
    "auth-status": string;
    "status-code": string;
    message: string;
    questions: any;
}

interface IDFL3SubmitResponseDTO {
    "auth-status": string;
    "status-code": string;
    message: string;
    data: string;
}

interface EZSGetQuestionFailCheck {
    isFailed: boolean;
    reason: string;
}

const L3SecurityQuestionComponent: React.FunctionComponent<IProps> = (props) => {
    const languagePreference = useSelector((state: ApplicationState) => state.loggedInUser && state.loggedInUser.userInfo?.preferencedLanguage);
    const status = {
        success: "success",
        error: "error",
        systemError: "system-error",
        l3LastTryFail: "l3-last-try-fail",
        l3LastTryTimemout: "l3-timeout-locked",
        l3AttemptFail: "l3-attempt-fail",
        l3Timeout: "l3-timeout",
        thinClient: "thin-client",
        manualAuthentication: "manual-authentication",
        locked: "locked",
        l3OtpLocked: "l3-otp-locked",
        inProgress: "in-progress",
        invalidSsn: "invalid-ssn",
        noQuestion: "no-question",
        lockedNotEnoughInfo: "locked-not-enough-info",
    };

    // utility function - start
    const timeToString = (time: number) => {
        // determine current minute
        let minutes = Math.floor(time / 60);
        // Detemine seconds left in minute
        let seconds = time % 60;
        // Add a zero in front if a single digit number
        // return constructed string
        return `${minutes}:${seconds < 10 ? `0${seconds}` : seconds}`;
    };

    const EZSGetL3QuestionFailCheck = (response: EZSL3QuestionDTO): EZSGetQuestionFailCheck => {
        // es6 destructuring - pulls out property values into variables, can rename properties to new variable names - 'property:newName'
        const authStatus = response.authenticationStatus;
        const attemptStatus = response.numberOfTriesAttemptedStatus == null ? [] : response.numberOfTriesAttemptedStatus;
        const hasError = response.hasError;

        let failed = false; // indicates whether or not, a fail view is to be displayed
        let reason = "";
        if (attemptStatus.indexOf("FAILED_THIN_CLIENT") > -1) {
            // Make check if Thin client --> display "not enough credit history view"
            reason = status.thinClient;
            failed = true;
        } else if (authStatus === "MARK_FOR_MANUAL_AUTH") {
            // If just Manual Auth --> display "default manual auth view"
            reason = status.manualAuthentication;
            failed = true;
        } else if (attemptStatus.length >= 2 && attemptStatus[1] === "FAILED_ATTEMPT") {
            // If failed second time --> display fail 2 view
            reason = status.l3LastTryFail;
            failed = true;
        } else if (attemptStatus.length >= 2 && attemptStatus[1] === "TIMEOUT") {
            // If failed second time --> display fail 2 view
            reason = status.l3LastTryTimemout;
            failed = true;
        } else if (attemptStatus.length >= 2) {
            // If two attempts have already been made--> display "default manual auth view"
            reason = status.manualAuthentication;
            failed = true;
        } else if (hasError) {
            // display system fail view
            reason = status.systemError;
            failed = true;
        }
        return { isFailed: failed, reason: reason };
    };

    const oneTimeCreditServiceids = [
        ezsServiceIds.experianCreditReportwithScore,
        ezsServiceIds.creditReportForEachOf3BureausWithScore,
        ezsServiceIds.experianAnnualCreditReportWithScore,
        ezsServiceIds.experianAnnualCreditReportForEachOf3BureausWithScore,
    ];
    const oneTimeCreditServiceCode = getActiveServiceCode(oneTimeCreditServiceids);

    const humanReadableTryCount: any = {
        "1": "first",
        "2": "second",
        "3": "third",
        "4": "fourth",
        "5": "fifth",
    };
    let intervalRef: any = null;
    // utility function - end

    let initialQuestionState = [
        {
            "question-id": "",
            "question-number": "",
            "question-text": "",
            options: [
                {
                    "option-id": "",
                    "option-text": "",
                    "option-choiceid": "",
                },
            ],
        },
    ];
    let intitalQuestionAnswerState = [
        {
            "question-id": "",
            "option-id": "",
            "question-number": "",
            "option-choiceid": "",
        },
    ];

    const partnerPhoneNumber: string = useSelector((state: ApplicationState) =>
        state.configurationState && state.configurationState.configuration.partnerConfig.phoneNumber
            ? state.configurationState.configuration.partnerConfig.phoneNumber
            : ""
    );
    const [isLoading, setIsLoading] = useState(false);
    const [questions, setQuestions] = useState(initialQuestionState);
    const [answers, setAnswers] = useState(intitalQuestionAnswerState);
    const [timeLeft, setTimeLeft] = useState(250);
    const [timeLeftString, setTimeLeftString] = useState("");
    const [questionLoaded, setQuestionLoaded] = useState(false);
    const [additionalVerificatonNeeded, setAdditionalVerificatonNeeded] = useState(false);
    const [allQuestionAnswered, setAllQuestionAnswered] = useState(true);

    const globalTryCount = useSelector((state: ApplicationState) => {
        if (state.csidUserStatusState) {
            return state.csidUserStatusState?.globalQuestionTryCount;
        }

        return 0;
    });
    const dispatch = useDispatch();
    const setGlobalTryCount = (tryCount: number) => dispatch(CSIDActions.actionCreators.setGlobalQuestionTryCount(tryCount));
    const { t: tLang } = useTranslation();

    let strCloseButtonText = tLang("dashboard.creditScore.l3SecurityQuestionComp.closeButtonText");
    let strSubmitButtonText = tLang("dashboard.creditScore.l3SecurityQuestionComp.submitButtonText");
    let blnOpenAsDialog = false;

    // set default value for props, if not passed
    if (props.closeButtonText != null) {
        strCloseButtonText = props.closeButtonText;
    }
    if (props.submitButtonText != null) {
        strSubmitButtonText = props.submitButtonText;
    }
    if (props.openAsDialog != null) {
        blnOpenAsDialog = props.openAsDialog;
    }

    function prepareAnswersForQuestion(questions: any) {
        const questionList = questions;
        if (props.prepareForIDFL3) {
            let ansObj = questionList.map((q: { [x: string]: any }) => ({
                "question-id": q["question-id"],
                "option-id": "",
            }));
            setAnswers(ansObj);
        } else {
            let ansObj = questionList.map((q: { [x: string]: any }) => ({
                "question-id": q["question-id"],
                "question-number": q["question-number"],
                "option-id": "",
                "option-choiceid": "",
            }));
            setAnswers(ansObj);
        }
    }

    function getL3SecurityQuestions() {
        setIsLoading(true);
        services
            .getL3SecurityQuestion(props.ssn, props.postponeActivation, props.dob, props.address, "", "", languagePreference)
            .then((data) => {
                setIsLoading(false);
                var responseObj;
                try {
                    responseObj = JSON.parse(data);
                } catch (e) {
                    responseObj = data;
                }
                // isIDFUser
                // providerResponse

                loadSecurityQuestions(responseObj);
            })
            .catch((error) => {
                setIsLoading(false);
                if (props.errorCallback != null) {
                    props.errorCallback({ status: status.error, data: { reason: status.systemError, detail: error } });
                }
            });
    }

    const getTimerMessage = () => {
        return (
            <p className="l3-questions-timer margin-top-l">
                {parse(tLang("dashboard.creditScore.l3SecurityQuestionComp.questionTimer", { timeLeftString: timeLeftString }))}
            </p>
        );
    };

    const loadSecurityQuestions = (responseObj: any) => {
        var providerResponse: EZSL3QuestionDTO | IDFL3QuestionDTO;
        if (responseObj.providerResponse != null) {
            if (props.prepareForIDFL3) {
                providerResponse = responseObj.providerResponse as IDFL3QuestionDTO;
            } else {
                providerResponse = responseObj.providerResponse as EZSL3QuestionDTO;
                const ezsGetQuestonFailCheck: EZSGetQuestionFailCheck = EZSGetL3QuestionFailCheck(providerResponse);
                if (ezsGetQuestonFailCheck.isFailed) {
                    if (props.errorCallback != null) {
                        props.errorCallback({ status: status.error, data: { reason: ezsGetQuestonFailCheck.reason, detail: responseObj } });
                    }
                    return;
                }
            }
            if (providerResponse.questions != null) {
                setQuestionLoaded(true);
                setQuestions(providerResponse.questions);
                prepareAnswersForQuestion(providerResponse.questions);
                // start the timer if configured
                if (props.displayTimerForTimeout && props.timesInSecondBeforeTimeout > 0) {
                    setTimeLeft(props.timesInSecondBeforeTimeout);
                    setTimeLeftString(timeToString(props.timesInSecondBeforeTimeout));
                }
                //Bug 15881: To fix add count on each time loaded the security questions
                const tempTryCount = props.currentTryCount + 1;
                setGlobalTryCount(tempTryCount);
            } else if (props.prepareForIDFL3) {
                if (!responseObj.isValidSsn) {
                    props.errorCallback({ status: status.invalidSsn, data: { reason: status.invalidSsn, detail: responseObj } });
                } else if (responseObj.providerResponse["auth-status"] === "locked") {
                    props.errorCallback({ status: status.locked, data: { reason: status.locked, detail: responseObj } });
                } else if (responseObj.providerResponse["auth-status"] === "l3-otp-locked") {
                    props.errorCallback({ status: status.l3OtpLocked, data: { reason: status.l3OtpLocked, detail: responseObj } });
                } else if (responseObj.providerResponse["auth-status"] === "locked-not-enough-info") {
                    props.errorCallback({ status: status.lockedNotEnoughInfo, data: { reason: status.lockedNotEnoughInfo, detail: responseObj } });
                } else if (responseObj.providerResponse["auth-status"] === "no-question") {
                    props.errorCallback({ status: status.noQuestion, data: { reason: status.noQuestion, detail: responseObj } });
                } else if (responseObj.providerResponse["auth-status"] == null) {
                    props.errorCallback({
                        status: status.error,
                        data: { reason: "Could not authenticate user. Please provide correct SSN.", detail: responseObj },
                    });
                }
            }
        } else {
            if (props.errorCallback != null) {
                props.errorCallback({ status: status.error, data: { reason: status.systemError, detail: responseObj } });
            }
        }
    };

    const ensureDataFetchedOnLoad = () => {
        if (props.loadedResponse) {
            loadSecurityQuestions(props.loadedResponse);
        } else {
            getL3SecurityQuestions();
        }
    };

    useEffect(ensureDataFetchedOnLoad, []);

    // in case of dialog, register event for escape press
    if (blnOpenAsDialog) {
        window.addEventListener("keyup", (evt: any) => {
            evt = evt || window.event;
            var isEscape = false;
            if ("key" in evt) {
                isEscape = evt.key === "Escape" || evt.key === "Esc";
            } else {
                isEscape = evt.keyCode === 27;
            }
            if (isEscape) {
                window.removeEventListener("keyup", () => { });
                onClose("cancel");
            }
        });
    }

    const invokePostSubmitCallback = (status: string, data: any) => {
        if (props.onSubmitCallback != null) {
            props.onSubmitCallback({ status: status, data: data });
        }
    };

    const isAllQuestionAnswered = (sourceSubmitbutton: boolean): boolean => {
        let returnValue = answers.filter((ans) => ans["option-id"] === "").length === 0;
        if (sourceSubmitbutton) {
            if (returnValue) {
                setAllQuestionAnswered(true);
            } else {
                setAllQuestionAnswered(false);
            }
        } else {
            if (!allQuestionAnswered) {
                if (returnValue) {
                    setAllQuestionAnswered(true);
                }
            }
        }

        return returnValue;
    };

    const onSubmit = () => {
        if (!isAllQuestionAnswered(true)) {
            return;
        }
        setIsLoading(true);
        services
            .submitL3SecurityQuestionAnswer(answers, props.postponeActivation, props.ssn, props.dob, props.address, props.tuTrustedSessionId)
            .then((data) => {
                if (!oneTimeCreditServiceCode) {
                    setIsLoading(false);
                }

                var responseObj: any;
                try {
                    responseObj = JSON.parse(data);
                } catch (e) {
                    responseObj = data;
                }

                var providerResponse: EZSL3SubmitResponseDTO | IDFL3SubmitResponseDTO;

                if (props.prepareForIDFL3) {
                    providerResponse = responseObj.providerResponse as IDFL3SubmitResponseDTO;
                    if (providerResponse["auth-status"] != null) {
                        const authStatus = providerResponse["auth-status"].toUpperCase().trim().replace(/[-]/g, "");
                        switch (authStatus) {
                            case "AUTHENTICATED":
                                invokePostSubmitCallback(status.success, { detail: responseObj });
                                break;
                            case "LOCKED":
                                invokePostSubmitCallback(status.locked, { reason: status.locked, detail: responseObj });
                                break;
                            case "L3OTPLOCKED":
                                invokePostSubmitCallback(status.l3OtpLocked, { reason: status.l3OtpLocked, detail: responseObj });
                                break;
                            case "IN PROGRESS":
                                invokePostSubmitCallback(status.inProgress, { detail: responseObj });
                                break;
                            default:
                                invokePostSubmitCallback(status.error, { reason: status.systemError, detail: responseObj });
                                break;
                        }
                    } else {
                        invokePostSubmitCallback(status.error, { reason: status.systemError, detail: responseObj });
                    }
                } else {
                    providerResponse = responseObj.providerResponse as EZSL3SubmitResponseDTO;
                    if (providerResponse.status != null) {
                        if (providerResponse.status === "SUCCESS") {
                            if (oneTimeCreditServiceCode) {
                                services.orderOneTimeCreditReport(oneTimeCreditServiceCode).then((data) => {
                                    setIsLoading(false);
                                    invokePostSubmitCallback(status.success, { detail: responseObj });
                                });
                            } else {
                                invokePostSubmitCallback(status.success, { detail: responseObj });
                            }
                        } else if (providerResponse.status === "FAIL") {
                            if (providerResponse.moreAuthenticationRequired) {
                                setAdditionalVerificatonNeeded(true);
                                setQuestions(providerResponse.questions);
                                prepareAnswersForQuestion(providerResponse.questions);
                            } else if (props.currentTryCount > 1) {
                                // If failed second time --> display fail 2 view
                                invokePostSubmitCallback(status.error, { reason: status.l3LastTryFail, detail: responseObj });
                            } else {
                                // Else if failed once --> display fail 1 view
                                invokePostSubmitCallback(status.error, { reason: status.l3AttemptFail, detail: responseObj });
                            }
                        } else if (providerResponse.hasError) {
                            invokePostSubmitCallback(status.error, { reason: status.systemError, detail: responseObj });
                        }
                    } else {
                        invokePostSubmitCallback(status.error, { reason: status.systemError, detail: responseObj });
                    }
                }
            })
            .catch((error) => {
                setIsLoading(false);
                invokePostSubmitCallback(status.error, { reason: status.systemError, detail: error });
            });
    };

    const onClose = (reason?: string) => {
        // execute method passed as props for onCloseButtonClick
        if (props.onCloseCallback != null) {
            props.onCloseCallback({ reason: reason == null ? "close" : "cancel" });
        }
    };
    const onTimeout = () => {
        setIsLoading(true);
        if (props.currentTryCount > 1) {
            // If failed second time --> display timeout 2 view
            if (props.onTimeoutCallback != null) {
                props.onTimeoutCallback({ status: status.error, data: { reason: status.l3LastTryTimemout } });
            }
        } else {
            // Else if failed once --> display timeout 1 view
            if (props.onTimeoutCallback != null) {
                props.onTimeoutCallback({ status: status.error, data: { reason: status.l3Timeout } });
            }
        }

        services
            .updateL3AttemptReason("TIMEOUT", "L3 Timer timed out")
            .then((data) => {
                setIsLoading(false);
            })
            .catch((error) => {
                setIsLoading(false);
            });

        if (props.currentTryCount > 1) {
            services
                .updateL3Status("l3-timeout-locked")
                .then((data) => {
                    setIsLoading(false);
                })
                .catch((error) => {
                    setIsLoading(false);
                });
        }
    };

    const updateTimer = () => {
        if (timeLeft > 0) {
            const tempTimeLeft = timeLeft - 1;
            // decrease time left by 1 second
            setTimeLeft(tempTimeLeft);
            // Update timer and time displayed
            setTimeLeftString(timeToString(tempTimeLeft));
        } else {
            if (intervalRef != null) {
                clearInterval(intervalRef);
            }
            // call api to update reason of timeout.
            onTimeout();
        }
    };

    useEffect(() => {
        if (props.displayTimerForTimeout && timeLeft >= 0) {
            intervalRef = setInterval(() => {
                updateTimer();
            }, 1000);
            return () => clearInterval(intervalRef);
        }
    });
    function handleAnswerSelectionChange(e: any) {
        const { name, value } = e.target;
        const question_id = name.replace("credit-question-", "");
        let newAnsArr = [...answers];
        newAnsArr.forEach(function (obj) {
            if (obj["question-id"] === question_id) {
                obj["option-id"] = value.split("_")[0];
                if (!props.prepareForIDFL3) {
                    obj["option-choiceid"] = value.split("_")[1];
                }
            }
        });
        setAnswers(newAnsArr);
        isAllQuestionAnswered(false);
    }

    return (
        <React.Fragment>
            <div className={`${isLoading && "loading"}`}>
                <div className="dialog-header">
                    {props.openAsDialog && (
                        <div className="dialog-icon status-default">
                            <RenderIcons iconName="question" />
                        </div>
                    )}
                    <h1>{tLang("dashboard.creditScore.l3SecurityQuestionComp.title")}</h1>
                </div>

                {questionLoaded && (
                    <React.Fragment>
                        <div className="dialog-body large-dialog-scroll">
                            {additionalVerificatonNeeded ? (
                                <h3>{tLang("dashboard.creditScore.l3SecurityQuestionComp.verificationNeeded", { questionsLength: questions.length })}</h3>
                            ) : (
                                <h3>{tLang("dashboard.creditScore.l3SecurityQuestionComp.verificationNotNeeded", { questionsLength: questions.length })}</h3>
                            )}

                            {props.displayTimerForTimeout && props.timesInSecondBeforeTimeout > 0 && getTimerMessage()}

                            {props.maxTryCount > 0 && props.maxTryCount < globalTryCount && (
                                <p className="l3-questions-timer">
                                    <span style={{ color: "#CC4E00" }}>
                                        {tLang("dashboard.creditScore.l3SecurityQuestionComp.maxTryCount", {
                                            humanReadableTryCount: humanReadableTryCount[props.maxTryCount.toString()],
                                        })}
                                    </span>
                                </p>
                            )}

                            {questions.map((question: any, index: any) => {
                                let className = "credit-question-text";

                                return (
                                    <div className="credit-question form-input col-gutter-vertical" key={question["question-id"]}>
                                        <p className={className}>
                                            <label htmlFor={`credit-question-${index}-0-` + question["question-id"]}>
                                                {index + 1}
                                                {". "}
                                                {question["question-text"]}
                                            </label>
                                        </p>
                                        {question.options.map((option: any, optionIndex: any) => {
                                            return (
                                                <div className="radio" key={option["option-id"]}>
                                                    <label className="form-radio-label">
                                                        <input
                                                            type="radio"
                                                            id={`credit-question-${index}-${optionIndex}-` + question["question-id"]}
                                                            name={"credit-question-" + question["question-id"]}
                                                            value={
                                                                option["option-choiceid"]
                                                                    ? option["option-id"] + "_" + option["option-choiceid"]
                                                                    : option["option-id"]
                                                            }
                                                            onChange={(e) => handleAnswerSelectionChange(e)}
                                                        />
                                                        <span className="form-radio-label-text">{" " + option["option-text"] + ' '.repeat(index)}</span>
                                                    </label>
                                                </div>
                                            );
                                        })}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="dialog-footer">
                            <div>
                                {!allQuestionAnswered && (
                                    <div className="form-error">
                                        {tLang("dashboard.creditScore.l3SecurityQuestionComp.makeSureAllQsAnswered")}
                                        <div className="space-divider margin-top-xs"></div>
                                    </div>
                                )}

                                {!props.prepareForIDFL3 && (
                                    <div>
                                        {tLang("dashboard.creditScore.l3SecurityQuestionComp.needHelp")}{" "}
                                        <a href={'tel:"' + partnerPhoneNumber + '"'}>{partnerPhoneNumber}</a>
                                    </div>
                                )}
                            </div>
                            <div className="row between-xs">
                                <div className="col-xs col-no-gutter flex-grow-null">
                                </div>
                                <div className="col-xs col-no-gutter flex-grow-null">
                                    <button type="button" className="btn-default btn-primary" onClick={onSubmit}>
                                        <span className="btn-text">{strSubmitButtonText}</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    </React.Fragment>
                )}
            </div>
        </React.Fragment>
    );
};

export default L3SecurityQuestionComponent;
